import { Dispatch } from 'redux';
import { getPatients, createPatient, updatePatient, deletePatient } from '../../services/api';
import { GetPatientsRequest, CreatePatientRequest, UpdatePatientRequest, PatientData, PaginationData } from '../../services/types';

// Action Types
export const FETCH_PATIENTS_REQUEST = 'FETCH_PATIENTS_REQUEST';
export const FETCH_PATIENTS_SUCCESS = 'FETCH_PATIENTS_SUCCESS';
export const FETCH_PATIENTS_FAILURE = 'FETCH_PATIENTS_FAILURE';

export const CREATE_PATIENT_REQUEST = 'CREATE_PATIENT_REQUEST';
export const CREATE_PATIENT_SUCCESS = 'CREATE_PATIENT_SUCCESS';
export const CREATE_PATIENT_FAILURE = 'CREATE_PATIENT_FAILURE';

export const UPDATE_PATIENT_REQUEST = 'UPDATE_PATIENT_REQUEST';
export const UPDATE_PATIENT_SUCCESS = 'UPDATE_PATIENT_SUCCESS';
export const UPDATE_PATIENT_FAILURE = 'UPDATE_PATIENT_FAILURE';

export const DELETE_PATIENT_REQUEST = 'DELETE_PATIENT_REQUEST';
export const DELETE_PATIENT_SUCCESS = 'DELETE_PATIENT_SUCCESS';
export const DELETE_PATIENT_FAILURE = 'DELETE_PATIENT_FAILURE';

export const SET_SEARCH_TERM = 'SET_SEARCH_TERM';
export const SET_CURRENT_PAGE = 'SET_CURRENT_PAGE';

// Action Interfaces
export interface FetchPatientsRequestAction {
  type: typeof FETCH_PATIENTS_REQUEST;
}

export interface FetchPatientsSuccessAction {
  type: typeof FETCH_PATIENTS_SUCCESS;
  payload: {
    patients: PatientData[];
    pagination: PaginationData;
  };
}

export interface FetchPatientsFailureAction {
  type: typeof FETCH_PATIENTS_FAILURE;
  payload: string;
}

export interface CreatePatientRequestAction {
  type: typeof CREATE_PATIENT_REQUEST;
}

export interface CreatePatientSuccessAction {
  type: typeof CREATE_PATIENT_SUCCESS;
  payload: PatientData;
}

export interface CreatePatientFailureAction {
  type: typeof CREATE_PATIENT_FAILURE;
  payload: string;
}

export interface UpdatePatientRequestAction {
  type: typeof UPDATE_PATIENT_REQUEST;
}

export interface UpdatePatientSuccessAction {
  type: typeof UPDATE_PATIENT_SUCCESS;
  payload: PatientData;
}

export interface UpdatePatientFailureAction {
  type: typeof UPDATE_PATIENT_FAILURE;
  payload: string;
}

export interface DeletePatientRequestAction {
  type: typeof DELETE_PATIENT_REQUEST;
}

export interface DeletePatientSuccessAction {
  type: typeof DELETE_PATIENT_SUCCESS;
  payload: string; // patient ID
}

export interface DeletePatientFailureAction {
  type: typeof DELETE_PATIENT_FAILURE;
  payload: string;
}

export interface SetSearchTermAction {
  type: typeof SET_SEARCH_TERM;
  payload: string;
}

export interface SetCurrentPageAction {
  type: typeof SET_CURRENT_PAGE;
  payload: number;
}

export type PatientActionTypes =
  | FetchPatientsRequestAction
  | FetchPatientsSuccessAction
  | FetchPatientsFailureAction
  | CreatePatientRequestAction
  | CreatePatientSuccessAction
  | CreatePatientFailureAction
  | UpdatePatientRequestAction
  | UpdatePatientSuccessAction
  | UpdatePatientFailureAction
  | DeletePatientRequestAction
  | DeletePatientSuccessAction
  | DeletePatientFailureAction
  | SetSearchTermAction
  | SetCurrentPageAction;

// Action Creators
export const fetchPatientsRequest = (): FetchPatientsRequestAction => ({
  type: FETCH_PATIENTS_REQUEST,
});

export const fetchPatientsSuccess = (
  patients: PatientData[],
  pagination: PaginationData
): FetchPatientsSuccessAction => ({
  type: FETCH_PATIENTS_SUCCESS,
  payload: { patients, pagination },
});

export const fetchPatientsFailure = (error: string): FetchPatientsFailureAction => ({
  type: FETCH_PATIENTS_FAILURE,
  payload: error,
});

export const createPatientRequest = (): CreatePatientRequestAction => ({
  type: CREATE_PATIENT_REQUEST,
});

export const createPatientSuccess = (patient: PatientData): CreatePatientSuccessAction => ({
  type: CREATE_PATIENT_SUCCESS,
  payload: patient,
});

export const createPatientFailure = (error: string): CreatePatientFailureAction => ({
  type: CREATE_PATIENT_FAILURE,
  payload: error,
});

export const updatePatientRequest = (): UpdatePatientRequestAction => ({
  type: UPDATE_PATIENT_REQUEST,
});

export const updatePatientSuccess = (patient: PatientData): UpdatePatientSuccessAction => ({
  type: UPDATE_PATIENT_SUCCESS,
  payload: patient,
});

export const updatePatientFailure = (error: string): UpdatePatientFailureAction => ({
  type: UPDATE_PATIENT_FAILURE,
  payload: error,
});

export const deletePatientRequest = (): DeletePatientRequestAction => ({
  type: DELETE_PATIENT_REQUEST,
});

export const deletePatientSuccess = (patientId: string): DeletePatientSuccessAction => ({
  type: DELETE_PATIENT_SUCCESS,
  payload: patientId,
});

export const deletePatientFailure = (error: string): DeletePatientFailureAction => ({
  type: DELETE_PATIENT_FAILURE,
  payload: error,
});

export const setSearchTerm = (searchTerm: string): SetSearchTermAction => ({
  type: SET_SEARCH_TERM,
  payload: searchTerm,
});

export const setCurrentPage = (page: number): SetCurrentPageAction => ({
  type: SET_CURRENT_PAGE,
  payload: page,
});

// Thunk Actions
export const fetchPatients = (params?: GetPatientsRequest) => {
  return async (dispatch: Dispatch<PatientActionTypes>) => {
    dispatch(fetchPatientsRequest());
    try {
      const response = await getPatients(params);
      if (response.success) {
        dispatch(fetchPatientsSuccess(response.data, response.pagination));
      } else {
        dispatch(fetchPatientsFailure(response.message || 'Failed to fetch patients'));
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch patients';
      dispatch(fetchPatientsFailure(errorMessage));
    }
  };
};

export const addPatient = (data: CreatePatientRequest) => {
  return async (dispatch: Dispatch<PatientActionTypes>) => {
    dispatch(createPatientRequest());
    try {
      const response = await createPatient(data);
      if (response.success) {
        dispatch(createPatientSuccess(response.data));
        return response;
      } else {
        dispatch(createPatientFailure(response.message || 'Failed to create patient'));
        throw new Error(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create patient';
      dispatch(createPatientFailure(errorMessage));
      throw error;
    }
  };
};

export const editPatient = (patientId: string, data: UpdatePatientRequest) => {
  return async (dispatch: Dispatch<PatientActionTypes>) => {
    dispatch(updatePatientRequest());
    try {
      const response = await updatePatient(patientId, data);
      if (response.success) {
        dispatch(updatePatientSuccess(response.data));
        return response;
      } else {
        dispatch(updatePatientFailure(response.message || 'Failed to update patient'));
        throw new Error(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update patient';
      dispatch(updatePatientFailure(errorMessage));
      throw error;
    }
  };
};

export const removePatient = (patientId: string) => {
  return async (dispatch: Dispatch<PatientActionTypes>) => {
    dispatch(deletePatientRequest());
    try {
      const response = await deletePatient(patientId);
      if (response.success) {
        dispatch(deletePatientSuccess(patientId));
        return response;
      } else {
        dispatch(deletePatientFailure(response.message || 'Failed to delete patient'));
        throw new Error(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete patient';
      dispatch(deletePatientFailure(errorMessage));
      throw error;
    }
  };
};

